import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, Eye, Check, X } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardContent, Badge } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { PermissionGuard } from '@components/PermissionGuard';
import eventService from '@services/eventService';
import { DeliveryOrder, Event, DELIVERY_ORDER_STATUS, AttendedFilter, DeliveryOrderAttendanceSummary } from '@/types/event';
import { statusBadgeVariant, attendanceState, attendanceLabel, nextAttendance, recountSummary } from './invitationUtils';

/** Cartão de contagem de comparecimento exibido acima da listagem. */
const AttendanceCount: React.FC<{ label: string; value: number; className: string }> = ({ label, value, className }) => (
  <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${className}`}>
    <strong>{value}</strong> {label}
  </span>
);

/**
 * Marcação de comparecimento na própria linha. São dois botões em vez de um switch porque
 * o estado é triplo: clicar no botão já ativo volta o convite para "não informado".
 */
const AttendanceToggle: React.FC<{
  value: boolean | null | undefined;
  saving: boolean;
  onPick: (clicked: boolean) => void;
}> = ({ value, saving, onPick }) => {
  const base = 'inline-flex items-center justify-center w-8 h-8 rounded-full border transition-colors disabled:opacity-50';
  return (
    <div className="inline-flex items-center gap-1" role="group" aria-label="Comparecimento ao evento">
      <button
        type="button"
        disabled={saving}
        aria-pressed={value === true}
        title={value === true ? 'Compareceu (clique para desmarcar)' : 'Marcar como compareceu'}
        onClick={() => onPick(true)}
        className={`${base} ${value === true ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-slate-300 text-slate-400 hover:border-emerald-400 hover:text-emerald-600'}`}
      >
        <Check size={16} />
      </button>
      <button
        type="button"
        disabled={saving}
        aria-pressed={value === false}
        title={value === false ? 'Não compareceu (clique para desmarcar)' : 'Marcar como não compareceu'}
        onClick={() => onPick(false)}
        className={`${base} ${value === false ? 'bg-rose-600 border-rose-600 text-white' : 'bg-white border-slate-300 text-slate-400 hover:border-rose-400 hover:text-rose-600'}`}
      >
        <X size={16} />
      </button>
    </div>
  );
};

// Listagem de convites: filtragem por evento, por status logístico (Pendente/Enviado/Entregue)
// e por comparecimento ao evento, que se marca na própria linha.
export const InvitationListView: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems] = useState<DeliveryOrder[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [summary, setSummary] = useState<DeliveryOrderAttendanceSummary | null>(null);
  const [attendanceError, setAttendanceError] = useState<string | null>(null);
  const [savingAttendance, setSavingAttendance] = useState<number | null>(null);
  const pageSize = 20;

  const eventId = searchParams.get('event_id') ? Number(searchParams.get('event_id')) : undefined;
  const statusId = searchParams.get('status_id') ? Number(searchParams.get('status_id')) : undefined;
  const attended = (searchParams.get('attended') as AttendedFilter | null) ?? undefined;

  useEffect(() => {
    eventService.listEvents(1, 100).then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await eventService.listDeliveryOrders({
        page: currentPage,
        limit: pageSize,
        eventId,
        statusId,
        search: search.trim() || undefined,
        attended,
      });
      setItems(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotalItems(result.pagination.total);
      setSummary(result.summary ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar convites');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, eventId, statusId, attended]);

  const setFilter = (key: 'event_id' | 'status_id' | 'attended', value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next);
    setCurrentPage(1);
  };

  /**
   * Marca o comparecimento direto da linha. Atualiza a tela primeiro e desfaz se o PATCH
   * falhar — clicar no estado já ativo desmarca (volta a "não informado").
   */
  const handleToggleAttendance = async (item: DeliveryOrder, clicked: boolean) => {
    const target = nextAttendance(item.attended, clicked);
    const previous = item.attended;
    setAttendanceError(null);
    setSavingAttendance(item.delivery_order_id);
    setItems((prev) => prev.map((i) =>
      i.delivery_order_id === item.delivery_order_id ? { ...i, attended: target } : i));
    try {
      const updated = await eventService.setDeliveryOrderAttendance(item.delivery_order_id, target);
      setItems((prev) => prev.map((i) =>
        i.delivery_order_id === item.delivery_order_id ? { ...i, attended: updated.attended } : i));
      // Os contadores são da listagem inteira, então recontam aqui em vez de recarregar tudo.
      setSummary((prev) => (prev ? recountSummary(prev, previous, updated.attended) : prev));
    } catch (err) {
      setItems((prev) => prev.map((i) =>
        i.delivery_order_id === item.delivery_order_id ? { ...i, attended: previous } : i));
      setAttendanceError(err instanceof Error ? err.message : 'Erro ao marcar o comparecimento');
    } finally {
      setSavingAttendance(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-h1">Convites</h1>
          <p className="text-slate-600">Convites de eventos — envio pela logística própria ou pelos Correios</p>
        </div>
        <PermissionGuard permission="events.write">
          <Button
            onClick={() => navigate(`/events/invitations/new${eventId ? `?event_id=${eventId}` : ''}`)}
            icon={<Plus size={20} />}
          >
            Novo Convite
          </Button>
        </PermissionGuard>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-2">
            <select
              className="input-field text-sm md:w-64"
              value={eventId ?? ''}
              onChange={(e) => setFilter('event_id', e.target.value)}
            >
              <option value="">Todos os eventos</option>
              {events.map((ev) => (
                <option key={ev.event_id} value={ev.event_id}>{ev.name}</option>
              ))}
            </select>
            <select
              className="input-field text-sm md:w-48"
              value={statusId ?? ''}
              onChange={(e) => setFilter('status_id', e.target.value)}
            >
              <option value="">Todos os status</option>
              <option value={DELIVERY_ORDER_STATUS.PENDING}>Pendente</option>
              <option value={DELIVERY_ORDER_STATUS.SHIPPED}>Enviado</option>
              <option value={DELIVERY_ORDER_STATUS.DELIVERED}>Entregue</option>
              <option value={DELIVERY_ORDER_STATUS.CANCELED}>Cancelado</option>
            </select>
            <select
              className="input-field text-sm md:w-52"
              value={attended ?? ''}
              onChange={(e) => setFilter('attended', e.target.value)}
            >
              <option value="">Todo comparecimento</option>
              <option value="true">Compareceu</option>
              <option value="false">Não compareceu</option>
              <option value="unknown">Não informado</option>
            </select>
            <Input
              placeholder="Buscar por convidado ou cidade..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setCurrentPage(1); loadItems(); } }}
              className="flex-1"
            />
            <Button onClick={() => { setCurrentPage(1); loadItems(); }} variant="secondary" icon={<Search size={18} />}>
              Buscar
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
      )}

      {attendanceError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{attendanceError}</div>
      )}

      {summary && (
        <div className="flex flex-wrap gap-2 text-sm">
          <AttendanceCount label="Compareceram" value={summary.attended} className="bg-emerald-50 text-emerald-700 border-emerald-200" />
          <AttendanceCount label="Não compareceram" value={summary.not_attended} className="bg-rose-50 text-rose-700 border-rose-200" />
          <AttendanceCount label="Não informado" value={summary.unknown} className="bg-slate-50 text-slate-600 border-slate-200" />
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <tr>
                <th className="text-left">ID</th>
                <th className="text-left">Convidado</th>
                <th className="text-left">Evento</th>
                <th className="text-left">Cidade</th>
                <th className="text-left">Transportadora</th>
                <th className="text-center">Status</th>
                <th className="text-center">Compareceu</th>
                <th className="text-center">Ações</th>
              </tr>
            </TableHeader>
            <tbody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">Carregando...</TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">Nenhum convite encontrado</TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.delivery_order_id}>
                    <TableCell className="font-mono text-sm">{item.delivery_order_id}</TableCell>
                    <TableCell className="font-medium">{item.recipient_name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{item.event_name || '—'}</TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {item.ship_addr_city ? `${item.ship_addr_city}${item.ship_addr_state ? `/${item.ship_addr_state}` : ''}` : '—'}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{item.provider_name || 'A definir'}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant={statusBadgeVariant(item.status_id)}>{item.status_label}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <PermissionGuard
                        permission="events.write"
                        fallback={<span className="text-sm text-slate-600">{attendanceLabel(attendanceState(item))}</span>}
                      >
                        <AttendanceToggle
                          value={item.attended}
                          saving={savingAttendance === item.delivery_order_id}
                          onPick={(clicked) => handleToggleAttendance(item, clicked)}
                        />
                      </PermissionGuard>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => navigate(`/events/invitations/${item.delivery_order_id}`)}
                          icon={<Eye size={16} />}
                        >
                          Detalhes
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">{totalItems} registro(s) encontrado(s)</p>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => p - 1)}>
              Anterior
            </Button>
            <span className="flex items-center px-3 text-sm text-slate-600">Página {currentPage} de {totalPages}</span>
            <Button variant="secondary" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)}>
              Próxima
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
